import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { Population } from '../types/iloe';
import { Community } from '../types/community';
import { TeamColor } from '../types/wargame-teams';
import { TEAM_COLORS } from '../utils/networkVisualization';

interface NetworkPanelProps {
  selectedId: string | null;
  selectedType: 'population' | 'community' | null;
  onClose: () => void;
  onInspectCommunity?: (community: Community) => void;
}

export const NetworkPanel: React.FC<NetworkPanelProps> = ({ selectedId, selectedType, onClose, onInspectCommunity }) => {
  const networkGraph = useGameStore(state => state.getNetworkGraph());
  const [targetId, setTargetId] = useState<string>('');
  const [pathResult, setPathResult] = useState<string[] | null>(null);

  // Helper to get data (mocking store access for now as discussed)
  const [data, setData] = useState<{ pop?: Population, comm?: Community } | null>(null);

  React.useEffect(() => {
    if (!selectedId) {
      setData(null);
      return;
    }

    if (selectedType === 'population') {
      const pop = networkGraph.getPopulation(selectedId);
      if (pop) setData({ pop });
    } else if (selectedType === 'community') {
      import('../data/communities').then(mod => {
        const comm = mod.COMMUNITIES.find(c => c.id === selectedId);
        if (comm) setData({ comm });
      });
    }
  }, [selectedId, selectedType, networkGraph]);

  const handleFindPath = () => {
    if (selectedId && targetId) {
      const path = networkGraph.findPath(selectedId, targetId);
      setPathResult(path);
    }
  };

  if (!selectedId) {
    return (
      <div className="w-80 bg-gray-800 border-l border-gray-700 p-4 text-white overflow-y-auto h-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Network Analysis</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">×</button>
        </div>
        <p className="text-gray-400">Select a node to view details.</p>
      </div>
    );
  }

  return (
    <div className="w-80 bg-gray-800 border-l border-gray-700 p-4 text-white overflow-y-auto flex flex-col h-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">
          {selectedType === 'population' ? 'Population Detail' : 'Community Detail'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-white">×</button>
      </div>

      {data?.pop && (
        <div className="space-y-4">
          <div className="bg-gray-700 p-3 rounded">
            <h3 className="font-bold text-lg">{data.pop.name}</h3>
            <div className="text-sm text-gray-300">ID: {data.pop.id}</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm">Belief:</span>
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: TEAM_COLORS[data.pop.belief || 'Neutral'] }}
              />
              <span className="text-sm">{data.pop.belief || 'Neutral'}</span>
            </div>
            <div className="mt-1 text-sm">Evaluative Limit: {data.pop.evaluativeLimit}</div>
          </div>

          <div>
            <h4 className="font-bold mb-2">Connections ({data.pop.networkedToPopulations.length})</h4>
            <div className="max-h-40 overflow-y-auto bg-gray-900 p-2 rounded text-sm">
              {data.pop.networkedToPopulations.map(id => (
                <div key={id} className="py-1 border-b border-gray-800 last:border-0">
                  {id}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <h4 className="font-bold mb-2">Pathfinding</h4>
            <input 
              type="text" 
              placeholder="Target Population ID" 
              className="w-full bg-gray-900 border border-gray-600 rounded p-2 text-sm mb-2"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
            />
            <button 
              onClick={handleFindPath}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2 rounded text-sm"
            >
              Find Path
            </button>
            
            {pathResult && (
              <div className="mt-2 bg-gray-900 p-2 rounded text-xs">
                {pathResult.length > 0 ? (
                  <div className="text-green-400">
                    Path found ({pathResult.length} hops):<br/>
                    {pathResult.join(' → ')}
                  </div>
                ) : (
                  <div className="text-red-400">No path found</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {data?.comm && (
        <div className="space-y-4">
          <div className="bg-gray-700 p-3 rounded">
            <h3 className="font-bold text-lg">{data.comm.name}</h3>
            <div className="text-sm text-gray-300">ID: {data.comm.id}</div>
            <div className="text-sm text-gray-300">Sector: {data.comm.sectorId}</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm">Owner:</span>
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: TEAM_COLORS[data.comm.ownerId || 'Neutral'] }}
              />
              <span className="text-sm">{data.comm.ownerId || 'Neutral'}</span>
            </div>
            
            <button 
              onClick={() => onInspectCommunity?.(data.comm!)}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2 rounded text-sm mt-4"
            >
              Inspect Community
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
